import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Save, Eye, Settings, Palette, Bell, Users, FileCheck } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const VendorPortal = () => {
  const [portalSettings, setPortalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const portalStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'draft', label: 'Draft' }
  ];

  const approverRoles = [
    { value: 'org_admin', label: 'Organization Admin' },
    { value: 'org_user', label: 'Organization User' }
  ];

  useEffect(() => {
    fetchPortalSettings();
  }, []);

  const fetchPortalSettings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user's organization
      const { data: orgMember } = await supabase?.from('organization_members')?.select('organization_id')?.eq('user_id', user?.id)?.single();

      if (!orgMember) throw new Error('User not associated with any organization');

      const { data, error } = await supabase?.from('vendor_portal_settings')?.select('*')?.eq('organization_id', orgMember?.organization_id)?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      if (data) {
        setPortalSettings(data);
      } else {
        // Create default settings if none exist
        const defaultSettings = {
          organization_id: orgMember?.organization_id,
          status: 'draft',
          theme_settings: {
            primaryColor: '#059669',
            secondaryColor: '#64748b',
            logo: null,
            customCss: ''
          },
          features_enabled: {
            invoiceSubmission: true,
            paymentStatus: true,
            documentUpload: true,
            contractManagement: false,
            complianceTracking: true
          },
          approval_workflow: {
            requireApproval: true,
            approverRoles: ['org_admin'],
            autoApprovalLimit: 0
          },
          notification_settings: {
            emailNotifications: true,
            approvalAlerts: true,
            paymentNotifications: true
          },
          onboarding_settings: {
            requireComplianceDocs: true,
            customOnboardingSteps: [],
            welcomeMessage: 'Welcome to our vendor portal'
          }
        };

        const { data: newData, error: insertError } = await supabase?.from('vendor_portal_settings')?.insert([defaultSettings])?.select()?.single();

        if (insertError) throw insertError;
        setPortalSettings(newData);
      }
    } catch (error) {
      console.error('Error fetching portal settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!portalSettings) return;

    try {
      setSaving(true);
      const { error } = await supabase?.from('vendor_portal_settings')?.update(portalSettings)?.eq('id', portalSettings?.id);

      if (error) throw error;
      
      alert('Vendor portal settings saved successfully!');
    } catch (error) {
      console.error('Error saving portal settings:', error);
      alert(`Error saving settings: ${error?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (path, value) => {
    setPortalSettings(prev => {
      const newSettings = { ...prev };
      const keys = path?.split('.');
      let current = newSettings;
      
      for (let i = 0; i < keys?.length - 1; i++) {
        if (!current?.[keys?.[i]]) current[keys[i]] = {};
        current = current?.[keys?.[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Portal Status
          </label>
          <Select
            value={portalSettings?.status || 'draft'}
            onChange={(e) => updateSettings('status', e?.target?.value)}
            options={portalStatusOptions}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Set to Active to make the portal available to vendors
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Custom Domain
          </label>
          <Input
            type="text"
            value={portalSettings?.custom_domain || ''}
            onChange={(e) => updateSettings('custom_domain', e?.target?.value)}
            placeholder="vendors.yourcompany.com"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Optional: Use your own domain for the vendor portal
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Welcome Message for Onboarding
        </label>
        <textarea
          value={portalSettings?.onboarding_settings?.welcomeMessage || ''}
          onChange={(e) => updateSettings('onboarding_settings.welcomeMessage', e?.target?.value)}
          placeholder="Enter a welcome message for new vendors"
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>
    </div>
  );

  const renderFeaturesTab = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-foreground mb-4">Available Features</h4>
        <div className="space-y-4">
          {[
            { key: 'invoiceSubmission', label: 'Invoice Submission', description: 'Allow vendors to submit invoices for approval' },
            { key: 'paymentStatus', label: 'Payment Status', description: 'Show payment status and history to vendors' },
            { key: 'documentUpload', label: 'Document Upload', description: 'Enable vendors to upload compliance and contract documents' },
            { key: 'contractManagement', label: 'Contract Management', description: 'Manage contracts and renewals' },
            { key: 'complianceTracking', label: 'Compliance Tracking', description: 'Track compliance documents and certifications' }
          ]?.map((feature) => (
            <div key={feature?.key} className="flex items-start space-x-3 p-4 bg-surface rounded-lg border border-border">
              <Checkbox
                checked={portalSettings?.features_enabled?.[feature?.key] || false}
                onChange={(checked) => updateSettings(`features_enabled.${feature?.key}`, checked)}
              />
              <div>
                <label className="text-sm font-medium text-foreground">{feature?.label}</label>
                <p className="text-xs text-muted-foreground">{feature?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderWorkflowTab = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-foreground mb-4">Approval Workflow</h4>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3 p-4 bg-surface rounded-lg border border-border">
            <Checkbox
              checked={portalSettings?.approval_workflow?.requireApproval || false}
              onChange={(checked) => updateSettings('approval_workflow.requireApproval', checked)}
            />
            <div>
              <label className="text-sm font-medium text-foreground">Require Approval for Submissions</label>
              <p className="text-xs text-muted-foreground">All vendor submissions must be approved before processing</p>
            </div>
          </div>

          {portalSettings?.approval_workflow?.requireApproval && (
            <>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Approver Roles
                </label>
                <div className="space-y-2">
                  {approverRoles?.map((role) => (
                    <div key={role?.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={portalSettings?.approval_workflow?.approverRoles?.includes(role?.value) || false}
                        onChange={(e) => {
                          const currentRoles = portalSettings?.approval_workflow?.approverRoles || [];
                          if (e?.target?.checked) {
                            updateSettings('approval_workflow.approverRoles', [...currentRoles, role?.value]);
                          } else {
                            updateSettings('approval_workflow.approverRoles', currentRoles?.filter(r => r !== role?.value));
                          }
                        }}
                        className="rounded border-border text-primary"
                      />
                      <span className="text-sm text-foreground">{role?.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Auto-Approval Limit ($)
                </label>
                <Input
                  type="number"
                  value={portalSettings?.approval_workflow?.autoApprovalLimit || 0}
                  onChange={(e) => updateSettings('approval_workflow.autoApprovalLimit', parseFloat(e?.target?.value) || 0)}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Invoices below this amount will be auto-approved. Set to 0 to require approval for all amounts.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderOnboardingTab = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-foreground mb-4">Vendor Onboarding</h4>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3 p-4 bg-surface rounded-lg border border-border">
            <Checkbox
              checked={portalSettings?.onboarding_settings?.requireComplianceDocs || false}
              onChange={(checked) => updateSettings('onboarding_settings.requireComplianceDocs', checked)}
            />
            <div>
              <label className="text-sm font-medium text-foreground">Require Compliance Documents</label>
              <p className="text-xs text-muted-foreground">Vendors must upload compliance documents during onboarding</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Custom Onboarding Steps
            </label>
            <textarea
              value={portalSettings?.onboarding_settings?.customOnboardingSteps?.join('\n') || ''}
              onChange={(e) => updateSettings('onboarding_settings.customOnboardingSteps', e?.target?.value?.split('\n')?.filter(step => step?.trim()))}
              placeholder="Upload Tax Certificate&#10;Complete W-9 Form&#10;Provide Insurance Documentation&#10;One step per line"
              rows={5}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Add custom steps to the vendor onboarding process, one per line.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderThemeTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Primary Color
          </label>
          <div className="flex items-center space-x-2">
            <Input
              type="color"
              value={portalSettings?.theme_settings?.primaryColor || '#059669'}
              onChange={(e) => updateSettings('theme_settings.primaryColor', e?.target?.value)}
              className="w-16 h-10"
            />
            <Input
              type="text"
              value={portalSettings?.theme_settings?.primaryColor || '#059669'}
              onChange={(e) => updateSettings('theme_settings.primaryColor', e?.target?.value)}
              placeholder="#059669"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Secondary Color
          </label>
          <div className="flex items-center space-x-2">
            <Input
              type="color"
              value={portalSettings?.theme_settings?.secondaryColor || '#64748b'}
              onChange={(e) => updateSettings('theme_settings.secondaryColor', e?.target?.value)}
              className="w-16 h-10"
            />
            <Input
              type="text"
              value={portalSettings?.theme_settings?.secondaryColor || '#64748b'}
              onChange={(e) => updateSettings('theme_settings.secondaryColor', e?.target?.value)}
              placeholder="#64748b"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Logo URL
        </label>
        <Input
          type="text"
          value={portalSettings?.theme_settings?.logo || ''}
          onChange={(e) => updateSettings('theme_settings.logo', e?.target?.value)}
          placeholder="https://example.com/logo.png"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Custom CSS
        </label>
        <textarea
          value={portalSettings?.theme_settings?.customCss || ''}
          onChange={(e) => updateSettings('theme_settings.customCss', e?.target?.value)}
          placeholder="Enter custom CSS for advanced styling"
          rows={6}
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
        />
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-foreground mb-4">Notification Settings</h4>
        <div className="space-y-4">
          {[
            { key: 'emailNotifications', label: 'Email Notifications', description: 'Send general email notifications to vendors' },
            { key: 'approvalAlerts', label: 'Approval Alerts', description: 'Notify vendors when submissions are approved or rejected' },
            { key: 'paymentNotifications', label: 'Payment Notifications', description: 'Notify vendors when payments are processed' }
          ]?.map((setting) => (
            <div key={setting?.key} className="flex items-start space-x-3 p-4 bg-surface rounded-lg border border-border">
              <Checkbox
                checked={portalSettings?.notification_settings?.[setting?.key] || false}
                onChange={(checked) => updateSettings(`notification_settings.${setting?.key}`, checked)}
              />
              <div>
                <label className="text-sm font-medium text-foreground">{setting?.label}</label>
                <p className="text-xs text-muted-foreground">{setting?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'features', label: 'Features', icon: FileCheck },
    { id: 'workflow', label: 'Approval Workflow', icon: Users },
    { id: 'onboarding', label: 'Onboarding', icon: Users },
    { id: 'theme', label: 'Theme', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Vendor Portal</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure your vendor-facing portal for invoice submissions and document management
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Preview Portal
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
      {/* Status Badge */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Status:</span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          portalSettings?.status === 'active' ?'bg-green-100 text-green-800' 
            : portalSettings?.status === 'inactive' ?'bg-red-100 text-red-800' :'bg-yellow-100 text-yellow-800'
        }`}>
          {portalSettings?.status?.charAt(0)?.toUpperCase() + portalSettings?.status?.slice(1) || 'Draft'}
        </span>
      </div>
      {/* Tabs */}
      <div className="bg-surface rounded-lg border border-border">
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6 overflow-x-auto" aria-label="Tabs">
            {tabs?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab?.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'features' && renderFeaturesTab()}
          {activeTab === 'workflow' && renderWorkflowTab()}
          {activeTab === 'onboarding' && renderOnboardingTab()}
          {activeTab === 'theme' && renderThemeTab()}
          {activeTab === 'notifications' && renderNotificationsTab()}
        </div>
      </div>
    </div>
  );
};

export default VendorPortal;